import { useState, useEffect } from 'react';
import { useRecoilState } from 'recoil';
import {
  ClockIcon,
  CalendarIcon,
  ChevronLeftIcon,
  ChevronRightIcon
} from '@heroicons/react/24/outline';

// Recoil State Import
import workOrderValueAtom from '../../atoms/workOrderValueAtom';


function FleetAppointmentCard({ appointments = [], fetchFleetAppointmentDetails }) {
  const [workOrder, setWorkOrder] = useRecoilState(workOrderValueAtom);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 2; // Number of appointments per page

  const defaultWorkOrder = {
    serviceBundle: { data: [], note: '' },
    serviceList: { data: [], note: '' },
    materialList: { data: [], note: '' },
    otherList: { data: [], note: '' },
    technicianCost: '0',
    estimatedCompletionDate: '',
    estimatedCompletionTime: ''
  }

  // Calculate pagination
  const totalPages = Math.ceil(appointments.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedAppointments = appointments.slice(startIndex, startIndex + itemsPerPage);

  // Handle page change
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  // Pagination Component
  const Pagination = () => {
    if (totalPages <= 1) return null;

    return (
      <div className="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6">
        {/* Mobile view */}
        <div className="flex flex-1 justify-between items-center sm:hidden">
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className={`relative inline-flex items-center rounded-md px-4 py-2 text-sm font-medium
              ${currentPage === 1
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-white text-gray-700 hover:bg-gray-50 border'}`}
          >
            Previous
          </button>
          <span className="text-sm text-gray-700">
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className={`relative ml-3 inline-flex items-center rounded-md px-4 py-2 text-sm font-medium
              ${currentPage === totalPages
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-white text-gray-700 hover:bg-gray-50 border'}`}
          >
            Next
          </button>
        </div>

        {/* Desktop view */}
        <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
          <div className="text-sm text-gray-700">
            <span className="font-medium">Page {currentPage}</span>
            <span className="mx-1">of</span>
            <span className="font-medium">{totalPages}</span>
          </div>

          <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className={`relative inline-flex items-center rounded-l-md px-3 py-2 text-sm font-medium
                ${currentPage === 1
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-white text-gray-500 hover:bg-gray-50 border'}`}
            >
              <ChevronLeftIcon className="h-4 w-4" aria-hidden="true" />
              <span className="sr-only">Previous</span>
            </button>

            {/* Page Numbers */}
            {[...Array(totalPages)].map((_, index) => {
              const pageNumber = index + 1;
              const isCurrentPage = currentPage === pageNumber;
              const isFirstPage = pageNumber === 1;
              const isLastPage = pageNumber === totalPages;
              const isWithinTwoPages = Math.abs(currentPage - pageNumber) <= 1;
              const showEllipsis = !isWithinTwoPages && !isFirstPage && !isLastPage;

              if (showEllipsis && (pageNumber === 2 || pageNumber === totalPages - 1)) {
                return (
                  <span
                    key={pageNumber}
                    className="relative inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border"
                  >
                    ...
                  </span>
                );
              }

              if (isFirstPage || isLastPage || isWithinTwoPages) {
                return (
                  <button
                    key={pageNumber}
                    onClick={() => handlePageChange(pageNumber)}
                    className={`relative inline-flex items-center px-4 py-2 text-sm font-medium
                      ${isCurrentPage
                        ? 'z-10 bg-customOne text-white focus:z-20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-customOne border-customOne'
                        : 'bg-white text-gray-900 hover:bg-gray-50 border'}`}
                  >
                    {pageNumber}
                  </button>
                );
              }

              return null;
            })}

            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className={`relative inline-flex items-center rounded-r-md px-3 py-2 text-sm font-medium
                ${currentPage === totalPages
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-white text-gray-500 hover:bg-gray-50 border'}`}
            >
              <span className="sr-only">Next</span>
              <ChevronRightIcon className="h-4 w-4" aria-hidden="true" />
            </button>
          </nav>
        </div>
      </div>
    );
  };

  return (
    <div className="bg-white rounded-lg shadow-sm">
      {/* Appointments List */}
      <div className="overflow-y-auto">
        {appointments.length === 0 ? (
          <div className="p-4 text-center text-gray-500">
            No appointments found
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 gap-3 p-4">
              {paginatedAppointments.map((appointment) => (
                <div
                  key={appointment.id}
                  onClick={() => {
                    fetchFleetAppointmentDetails(appointment.id);
                    setWorkOrder(defaultWorkOrder);
                  }}
                  className="bg-gray-50 p-4 rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors cursor-pointer"
                >
                  {/* Top Row */}
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="text-sm font-medium text-gray-900">
                        {appointment.make} {appointment.model}
                      </h3>
                      <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                        <span className="flex items-center">
                          <CalendarIcon className="h-3.5 w-3.5 mr-1" />
                          {appointment.date}
                        </span>
                        <span className="flex items-center">
                          <ClockIcon className="h-3.5 w-3.5 mr-1" />
                          {appointment.time}
                        </span>
                      </div>
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs ${appointment.status === 'Completed' ? 'bg-green-100 text-green-700' : appointment.status === 'Pending' ? 'bg-yellow-100 text-yellow-500' : 'bg-blue-50 text-blue-700'}`}>
                      {appointment.status}
                    </span>
                  </div>
                  {appointment.problemDescription && (
                    <div className="mt-2 text-xs text-gray-600 line-clamp-2">
                      {appointment.problemDescription}
                    </div>
                  )}
                  {/* <p className="text-xs mt-2 text-gray-600">
                    Work Estimate Status:
                    {appointment?.pilot_estimate === null ? <span className="text-red-500 ml-2 bg-red-100 px-2 py-1 rounded-full">Not Created</span> : <span className="text-green-500 ml-2 bg-green-100 rounded-full">{appointment?.pilot_estimate?.status === "Pending" ? <span className="text-yellow-500 bg-yellow-100 px-2 py-1 rounded-full">Pending</span> : <span className="text-green-500 bg-green-100 px-2 py-1 rounded-full">Approved</span>}</span>}

                  </p> */}

                  {/* Bottom Info */}
                  <div className="mt-3 pt-3 border-t border-gray-200 flex justify-between items-center">
                    <div>
                      <div className="text-xs text-gray-500">
                        Booking ID: {appointment.bookingId}
                      </div>

                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();

                        setWorkOrder(defaultWorkOrder);

                        fetchFleetAppointmentDetails(appointment.id);
                      }}
                      className="text-xs text-customOne hover:text-customOneDark font-medium"
                    >
                      View Details →
                    </button>
                  </div>
                </div>
              ))}
            </div>
            <Pagination />
          </>
        )}
      </div>
    </div>
  );
}

export default FleetAppointmentCard;